<template>
  <div class="container-fluid p-0">
    <div class="row">
      <div class="col-12">
        <div class="card my-4">
          <div class="card-body p-0">
            <div class="terminal-container">

              <!-- Header -->
              <div class="terminal-header">
                <div class="terminal-controls">
                  <span class="control close"></span>
                  <span class="control minimize"></span>
                  <span class="control maximize"></span>
                </div>
                <div class="terminal-title">
                  <i class="fas fa-lock"></i>
                  rifa365@semaphore:~$ watch -n5 mysql_lock_history
                </div>
                <div class="terminal-info">
                  <span :class="isConnected ? 'status-ok' : 'status-err'">
                    {{ isConnected ? '● Conectado' : '● Desconectado' }}
                  </span>
                </div>
              </div>

              <!-- Summary Bar -->
              <div class="summary-bar">
                <div class="summary-item">
                  <span class="summary-label">Locks ativos agora</span>
                  <span class="summary-value" :class="activeCount > 0 ? 'value-warning' : 'value-ok'">
                    {{ activeCount }}
                  </span>
                </div>
                <div class="summary-item">
                  <span class="summary-label">Eventos no histórico</span>
                  <span class="summary-value">{{ allEvents.length }}</span>
                </div>
                <div class="summary-item">
                  <span class="summary-label">Total de compras</span>
                  <span class="summary-value value-ok">{{ acquiredCount }}</span>
                </div>
                <div class="summary-item">
                  <span class="summary-label">Timeouts</span>
                  <span class="summary-value" :class="timeoutCount > 0 ? 'value-warning' : ''">
                    {{ timeoutCount }}
                  </span>
                </div>
                <div class="summary-item">
                  <span class="summary-label">Última atualização</span>
                  <span class="summary-value">{{ lastUpdate }}</span>
                </div>
                <div class="summary-actions">
                  <button @click="fetchStatus" class="btn-terminal" :disabled="loading">
                    <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
                    Atualizar
                  </button>
                  <button @click="toggleAutoRefresh" class="btn-terminal" :class="{ 'active': autoRefresh }">
                    {{ autoRefresh ? '⏸ Auto 5s' : '▶ Auto 5s' }}
                  </button>
                </div>
              </div>

              <!-- Terminal Content -->
              <div class="terminal-content" ref="terminalContent">

                <!-- Loading -->
                <div class="log-line system" v-if="loading && allEvents.length === 0">
                  <span class="timestamp">{{ now() }}</span>
                  <span class="level system">SYSTEM</span>
                  <span class="message">
                    Consultando histórico de locks...
                    <span class="loading-dots"><span></span><span></span><span></span></span>
                  </span>
                </div>

                <!-- Locks vivos -->
                <template v-if="activeLocks.length > 0">
                  <div class="log-line log-warning" v-for="lock in activeLocks" :key="lock.thread_id">
                    <span class="timestamp">{{ now() }}</span>
                    <span class="level warning">ATIVO</span>
                    <span class="user">thread#{{ lock.thread_id }}</span>
                    <span class="message">
                      <span class="log-module" style="color:#f1fa8c">
                        [CAMPANHA {{ getCampaignId(lock.lock_name) }}]
                      </span>
                      <span class="log-action"> [{{ lock.grant_status }}]</span>
                      Lock ativo agora: <span class="log-name">{{ lock.lock_name }}</span>
                    </span>
                  </div>
                </template>

                <!-- Histórico de eventos do backend -->
                <template v-if="allEvents.length > 0">
                  <div class="section-divider">
                    <span>── HISTÓRICO DE EVENTOS (últimos {{ allEvents.length }}) ──</span>
                  </div>
                  <div
                    v-for="(ev, idx) in allEvents"
                    :key="idx"
                    class="log-line"
                    :class="getEventRowClass(ev.event)"
                  >
                    <span class="timestamp">{{ formatTs(ev.at) }}</span>
                    <span class="level" :class="getEventLevelClass(ev.event)">
                      {{ ev.event }}
                    </span>
                    <span class="user">camp.{{ ev.campaign_id }}</span>
                    <span class="message">
                      <span :class="getEventTextClass(ev.event)">
                        {{ getEventText(ev) }}
                      </span>
                    </span>
                  </div>
                </template>

                <!-- Nenhum evento ainda -->
                <div class="log-line system" v-if="!loading && allEvents.length === 0 && activeLocks.length === 0">
                  <span class="timestamp">{{ now() }}</span>
                  <span class="level system">SYSTEM</span>
                  <span class="message">
                    Nenhum evento registrado ainda.
                    Os eventos aparecerão aqui conforme as compras forem sendo realizadas.
                    <span class="cursor">_</span>
                  </span>
                </div>
              </div>

              <!-- Footer -->
              <div class="terminal-footer">
                <span>Fonte: memória do servidor (últimos 100 eventos)</span>
                <span>•</span>
                <span>{{ autoRefresh ? 'Auto-refresh: 5s' : 'Manual' }}</span>
                <div class="footer-actions">
                  <button @click="scrollToBottom" class="btn-footer">↓</button>
                  <button @click="scrollToTop" class="btn-footer">↑</button>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "@/configs/AxiosConfig";

export default {
  name: "SemaphoreControl",
  data() {
    return {
      activeLocks: [],
      activeCount: 0,
      allEvents: [],
      loading: false,
      isConnected: true,
      autoRefresh: false,
      autoRefreshInterval: null,
      lastUpdate: "—",
    };
  },
  computed: {
    acquiredCount() {
      return this.allEvents.filter(e => e.event === "ACQUIRED").length;
    },
    timeoutCount() {
      return this.allEvents.filter(e => e.event === "TIMEOUT").length;
    },
  },
  mounted() {
    this.fetchStatus();
  },
  beforeUnmount() {
    if (this.autoRefreshInterval) clearInterval(this.autoRefreshInterval);
  },
  methods: {
    async fetchStatus() {
      try {
        this.loading = true;
        const { data } = await http.get("/admin/semaphore/status");

        this.activeLocks = data.active_locks || [];
        this.activeCount = data.active_count || 0;
        this.allEvents = data.history || [];
        this.isConnected = true;
        this.lastUpdate = this.now();

        this.$nextTick(() => this.scrollToTop());
      } catch (e) {
        console.error("Erro ao buscar semáforo:", e);
        this.isConnected = false;
      } finally {
        this.loading = false;
      }
    },

    toggleAutoRefresh() {
      this.autoRefresh = !this.autoRefresh;
      if (this.autoRefresh) {
        this.fetchStatus();
        this.autoRefreshInterval = setInterval(() => this.fetchStatus(), 5000);
      } else {
        clearInterval(this.autoRefreshInterval);
        this.autoRefreshInterval = null;
      }
    },

    getCampaignId(lockName) {
      if (!lockName) return "?";
      return lockName.startsWith("campaign_") ? lockName.slice(9) : lockName;
    },

    getEventText(ev) {
      const detailsStr = ev.details ? ` [${ev.details}]` : '';
      const campIdStr = ev.campaign_name && ev.campaign_name !== "Desconhecida" 
                        ? `"${ev.campaign_name}" (ID: ${ev.campaign_id})` 
                        : `${ev.campaign_id}`;
      
      let durationStr = "";
      if (ev.duration_ms > 0) {
        durationStr = ` (duração: ${(ev.duration_ms / 1000).toFixed(3)}s)`;
      } else if (ev.event === "RELEASED") {
        durationStr = ` (duração: 0s)`;
      }

      switch (ev.event) {
        case "ACQUIRED":
          return `Lock adquirido para campanha ${campIdStr}${detailsStr}`;
        case "RELEASED":
          return `Lock liberado — campanha ${campIdStr}${durationStr}${detailsStr}`;
        case "TIMEOUT":
          return `Timeout aguardando lock da campanha ${campIdStr} — sistema ocupado${detailsStr}`;
        case "ERROR":
          return `Erro ao operar lock da campanha ${campIdStr}${detailsStr}`;
        default:
          return `Evento desconhecido — campanha ${campIdStr}${detailsStr}`;
      }
    },

    getEventRowClass(event) {
      switch (event) {
        case "ACQUIRED":  return "log-info";
        case "RELEASED":  return "log-success-row";
        case "TIMEOUT":   return "log-warning";
        case "ERROR":     return "log-error-row";
        default:          return "";
      }
    },

    getEventLevelClass(event) {
      switch (event) {
        case "ACQUIRED":  return "info";
        case "RELEASED":  return "success";
        case "TIMEOUT":   return "warning";
        case "ERROR":     return "error";
        default:          return "system";
      }
    },

    getEventTextClass(event) {
      switch (event) {
        case "ACQUIRED":  return "log-info-text";
        case "RELEASED":  return "log-success";
        case "TIMEOUT":   return "log-value";
        case "ERROR":     return "log-error-text";
        default:          return "";
      }
    },

    formatTs(isoString) {
      if (!isoString) return "—";
      try {
        return new Date(isoString).toLocaleString("pt-BR", {
          day: "2-digit", month: "2-digit", year: "numeric",
          hour: "2-digit", minute: "2-digit", second: "2-digit",
          hour12: false,
        });
      } catch { return isoString; }
    },

    now() {
      return new Date().toLocaleString("pt-BR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
        hour12: false,
      });
    },

    scrollToBottom() {
      if (this.$refs.terminalContent)
        this.$refs.terminalContent.scrollTop = this.$refs.terminalContent.scrollHeight;
    },
    scrollToTop() {
      if (this.$refs.terminalContent)
        this.$refs.terminalContent.scrollTop = 0;
    },
  },
};
</script>

<style scoped>
.terminal-container {
  background: #282a36;
  border-radius: 8px;
  overflow: hidden;
  font-family: "JetBrains Mono", "Fira Code", "Courier New", monospace;
  font-size: 13px;
  min-height: 500px;
  display: flex;
  flex-direction: column;
}

.terminal-header {
  background: #1e1f29;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid #44475a;
}

.terminal-controls { display: flex; gap: 6px; }
.control { width: 12px; height: 12px; border-radius: 50%; }
.control.close    { background: #ff5f57; }
.control.minimize { background: #febc2e; }
.control.maximize { background: #28c840; }

.terminal-title { flex: 1; color: #f8f8f2; font-size: 12px; opacity: 0.8; }
.terminal-title i { margin-right: 8px; color: #bd93f9; }
.terminal-info { font-size: 11px; }
.status-ok  { color: #50fa7b; }
.status-err { color: #ff5555; }

.summary-bar {
  background: #1e1f29;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 20px;
  border-bottom: 1px solid #44475a;
  flex-wrap: wrap;
}

.summary-item { display: flex; flex-direction: column; gap: 2px; }
.summary-label { color: #6272a4; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
.summary-value { color: #f8f8f2; font-size: 13px; font-weight: 600; }
.value-ok      { color: #50fa7b; }
.value-warning { color: #ffb86c; }
.summary-actions { margin-left: auto; display: flex; gap: 8px; }

.terminal-content {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
  min-height: 360px;
  max-height: 560px;
}

.section-divider {
  color: #44475a;
  font-size: 11px;
  padding: 8px 0 4px;
  text-align: center;
  border-top: 1px solid #44475a;
  margin-top: 6px;
}

.log-line {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 3px 0;
  border-bottom: 1px solid rgba(68, 71, 90, 0.25);
  line-height: 1.5;
}

.timestamp { color: #6272a4; font-size: 11px; white-space: nowrap; min-width: 150px; }

.level {
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 3px;
  white-space: nowrap;
  text-transform: uppercase;
  min-width: 68px;
  text-align: center;
}

.level.system  { background: #44475a;   color: #6272a4; }
.level.info    { background: #8be9fd22; color: #8be9fd; border: 1px solid #8be9fd44; }
.level.success { background: #50fa7b22; color: #50fa7b; border: 1px solid #50fa7b44; }
.level.warning { background: #ffb86c22; color: #ffb86c; border: 1px solid #ffb86c44; }
.level.error   { background: #ff555522; color: #ff5555; border: 1px solid #ff555544; }

.user { color: #50fa7b; font-size: 11px; white-space: nowrap; min-width: 80px; }
.message { color: #f8f8f2; flex: 1; word-break: break-all; }

.log-module   { font-weight: 700; }
.log-action   { color: #8be9fd; }
.log-name     { color: #bd93f9; }
.log-value    { color: #f1fa8c; }
.log-success  { color: #50fa7b; }
.log-info-text   { color: #8be9fd; }
.log-error-text  { color: #ff5555; }

.log-warning     { background: rgba(255, 184, 108, 0.05); }
.log-success-row { background: rgba(80, 250, 123, 0.04); }
.log-error-row   { background: rgba(255, 85, 85, 0.05); }

.terminal-footer {
  background: #1e1f29;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 11px;
  color: #6272a4;
  border-top: 1px solid #44475a;
}

.footer-actions { margin-left: auto; display: flex; gap: 6px; }

.btn-terminal, .btn-footer {
  background: #44475a;
  border: none;
  color: #f8f8f2;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.2s;
  font-family: inherit;
}
.btn-terminal:hover, .btn-footer:hover { background: #6272a4; }
.btn-terminal.active { background: #50fa7b22; color: #50fa7b; border: 1px solid #50fa7b44; }
.btn-terminal:disabled { opacity: 0.5; cursor: not-allowed; }

.loading-dots span {
  display: inline-block;
  width: 4px; height: 4px;
  background: #6272a4;
  border-radius: 50%;
  margin: 0 2px;
  animation: bounce 1.2s infinite;
}
.loading-dots span:nth-child(2) { animation-delay: 0.2s; }
.loading-dots span:nth-child(3) { animation-delay: 0.4s; }

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0); }
  40%           { transform: scale(1); }
}

.cursor { animation: blink 1s infinite; color: #50fa7b; }
@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
</style>
